import React, { useContext } from "react";
import ScreenShareToggleButton from "./ScreenShareToggleButton";
import LeaveIconButton from "../LeaveIconButton";
import CameraToggleButton from "../CameraToggleButton";
import AudioToggleButton from "../AudioToggleButton";
import RecordingButton from "./RecordingButton";
import { CommonContext } from "hooks/CommonContext";
import { ConditionContext } from "hooks/ConditionContext";
import { MdAutoFixHigh } from "react-icons/md";

/* ─────────────────────────────────────────────────────────────────────────────
   All .ftr-* classes are defined here and shared by every child button file
   (ScreenShareToggleButton, RecordingButton, AudioToggleButton, etc.)
   so there is a single source of truth for the footer design.
───────────────────────────────────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600&display=swap');

  /* Bar */
  .ftr-bar {
    flex-shrink: 0;
    height: 72px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 0 24px;
    background: #0b1120;
    border-top: 1px solid #1a2540;
  }

  /* Pill container */
  .ftr-group {
    display: flex;
    align-items: center;
    gap: 4px;
    background: #101827;
    border: 1px solid #1e2d4a;
    border-radius: 50px;
    padding: 5px 7px;
  }

  /* Base button — used by ALL footer icon buttons */
  .ftr-btn {
    all: unset;
    box-sizing: border-box;
    position: relative;
    width: 44px;
    height: 44px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 19px;
    color: #c4d0ea;
    background: #162035;
    border: 1px solid #243354;
    transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.12s;
    flex-shrink: 0;
  }

  .ftr-btn:hover {
    background: #1e3050;
    color: #f0f4ff;
    border-color: #2e4472;
    transform: translateY(-1px);
  }

  .ftr-btn:active { transform: translateY(0); }

  /* Muted/off — red tint so user clearly knows it's off */
  .ftr-btn.ftr-off {
    background: rgba(239, 68, 68, 0.13);
    color: #fca5a5;
    border-color: rgba(239, 68, 68, 0.28);
  }

  /* Active/on — cyan tint */
  .ftr-btn.ftr-on {
    background: rgba(34, 211, 238, 0.13);
    color: #22d3ee;
    border-color: rgba(34, 211, 238, 0.28);
  }

  /* Recording live */
  .ftr-btn.ftr-rec {
    background: rgba(239, 68, 68, 0.15);
    color: #f87171;
    border-color: rgba(239, 68, 68, 0.32);
  }

  .ftr-btn:disabled {
    opacity: 0.28;
    cursor: not-allowed;
    transform: none;
  }

  /* Icons scale uniformly */
  .ftr-btn svg {
    width: 20px !important;
    height: 20px !important;
    display: block;
  }

  /* SVG image assets — invert so dark assets show white on dark bg */
  .ftr-btn img {
    width: 22px !important;
    height: 22px !important;
    display: block;
    filter: invert(1) brightness(2);
  }

  /* Recording live dot */
  .ftr-rec-dot {
    position: absolute;
    top: 5px;
    right: 5px;
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: #f87171;
    border: 1.5px solid #0b1120;
    animation: ftrRecPulse 1.2s ease-in-out infinite;
  }

  @keyframes ftrRecPulse {
    0%, 100% { opacity: 1;    transform: scale(1);    }
    50%       { opacity: 0.4; transform: scale(0.72); }
  }

  /* ── Mobile footer ── */
  @media (max-width: 768px) {
    .ftr-bar {
      position: fixed;
      bottom: 0;
      left: 0;
      right: 0;
      z-index: 200;
      height: auto;
      min-height: 64px;
      padding: 8px 12px;
      padding-bottom: calc(8px + env(safe-area-inset-bottom, 0px));
      gap: 6px;
      justify-content: space-around;
    }

    .ftr-group {
      gap: 2px;
      padding: 4px 5px;
    }

    .ftr-btn {
      width: 38px;
      height: 38px;
      font-size: 16px;
    }

    .ftr-btn svg {
      width: 17px !important;
      height: 17px !important;
    }

    .ftr-leave {
      padding: 8px 14px;
      font-size: 12.5px;
    }
  }

  /* Leave button */
  .ftr-leave {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 22px;
    background: rgba(220, 38, 38, 0.13);
    color: #fca5a5;
    border: 1px solid rgba(220, 38, 38, 0.28);
    border-radius: 50px;
    font-family: 'Inter', system-ui, sans-serif;
    font-size: 13.5px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    letter-spacing: 0.01em;
    transition: background 0.15s, color 0.15s, border-color 0.15s, transform 0.12s;
  }

  .ftr-leave:hover {
    background: rgba(220, 38, 38, 0.26);
    color: #fecaca;
    border-color: rgba(220, 38, 38, 0.48);
    transform: translateY(-1px);
  }

  .ftr-leave:active { transform: translateY(0); }

  /* Effects button active glow */
  .ftr-btn.ftr-effects-on {
    background: rgba(168, 85, 247, 0.15);
    color: #c084fc;
    border-color: rgba(168, 85, 247, 0.35);
    box-shadow: 0 0 12px rgba(168, 85, 247, 0.2);
  }

  .ftr-btn.ftr-effects-on:hover {
    background: rgba(168, 85, 247, 0.25);
    color: #d8b4fe;
    border-color: rgba(168, 85, 247, 0.5);
  }
`;

const EFFECTS_NAV = "effects";

let _inj = false;
const inject = () => {
  if (_inj) return;
  const s = document.createElement("style");
  s.textContent = STYLES;
  document.head.appendChild(s);
  _inj = true;
};

const Index = () => {
  inject();
  const { userOwnData } = useContext(CommonContext);
  const { openedComponentDrawerSetter, openedComponentDrawer } = useContext(ConditionContext);

  const isEffectsOpen = openedComponentDrawer === EFFECTS_NAV;

  const toggleEffects = () => {
    openedComponentDrawerSetter(isEffectsOpen ? "" : EFFECTS_NAV);
  };

  return (
    <div className="ftr-bar">
      {/* Group 1: Audio + Camera */}
      <div className="ftr-group">
        <AudioToggleButton />
        <CameraToggleButton />
      </div>

      {/* Group 2: Screen share + Recording (interviewers only) */}
      <div className="ftr-group">
        <ScreenShareToggleButton />
        {!userOwnData.isCandidate && <RecordingButton />}
      </div>

      {/* Group 3: Effects */}
      <div className="ftr-group">
        <button
          className={`ftr-btn${isEffectsOpen ? " ftr-effects-on" : ""}`}
          onClick={toggleEffects}
          title="Video Effects"
        >
          <MdAutoFixHigh />
        </button>
      </div>

      {/* Leave */}
      <LeaveIconButton />
    </div>
  );
};

export default Index;